import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, History, Search, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

const PAGE = 25;

const actionColor = (a) => {
  if (!a) return 'bg-gray-100 text-gray-700';
  if (a.includes('reject') || a.includes('failed') || a.includes('disabled')) return 'bg-red-100 text-red-700';
  if (a.includes('approved') || a.includes('verified') || a.includes('activated') || a.includes('credited') || a.includes('paid')) return 'bg-green-100 text-green-700';
  if (a.includes('rate') || a.includes('settings') || a.includes('changed')) return 'bg-blue-100 text-blue-700';
  return 'bg-slate-100 text-slate-700';
};

export default function AdminAuditLogsPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState('all');
  const [q, setQ] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE, offset });
      if (action !== 'all') params.append('action', action);
      if (q.trim()) params.append('q', q.trim());
      const res = await api.get(`/admin/audit-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      if (res.data.actions?.length) setActions(res.data.actions);
    } catch {
      /* handled by interceptor */
    } finally {
      setLoading(false);
    }
  }, [api, offset, action, q]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const fmt = (d) => (!d ? '-' : new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><History className="w-6 h-6" />Audit Logs</h1>
        <p className="text-muted-foreground text-sm">Every wallet, rate, funding and payout action, immutably recorded</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search description..." value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }} className="pl-9" data-testid="audit-search" />
            </div>
            <Select value={action} onValueChange={(v) => { setOffset(0); setAction(v); }}>
              <SelectTrigger className="w-[220px]" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {actions.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchLogs} data-testid="audit-refresh"><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No audit records found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-3 min-w-[150px]">Time</th>
                    <th className="text-left py-2 px-3 min-w-[160px]">Actor</th>
                    <th className="text-left py-2 px-3 min-w-[160px]">Action</th>
                    <th className="text-left py-2 px-3">Entity</th>
                    <th className="text-left py-2 px-3 min-w-[240px]">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`audit-row-${l.id}`}>
                      <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{fmt(l.created_at)}</td>
                      <td className="py-2 px-3"><p className="font-medium">{l.actor_email || '-'}</p><p className="text-xs text-muted-foreground capitalize">{(l.actor_role || '').replace(/_/g, ' ')}</p></td>
                      <td className="py-2 px-3"><Badge className={`${actionColor(l.action)} border-none text-[10px]`}>{(l.action || '').replace(/_/g, ' ')}</Badge></td>
                      <td className="py-2 px-3"><span className="text-xs">{l.entity_type}</span> <span className="font-mono text-xs text-muted-foreground">{l.entity_id?.slice(0, 10)}</span></td>
                      <td className="py-2 px-3">{l.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-muted-foreground">{total} records</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} data-testid="audit-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-muted-foreground">{Math.floor(offset / PAGE) + 1} / {Math.max(1, Math.ceil(total / PAGE))}</span>
              <Button variant="outline" size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} data-testid="audit-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
